import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { MagnifyingGlass, X, Hash, ClockCounterClockwise, User, ChatCircle, Target, GraduationCap, ArrowRight } from "@phosphor-icons/react";
import { api } from "../lib/api";

const RECENT_KEY = "hc_recent_searches";
const MAX_RECENT = 5;

/**
 * Cmd+K (or Ctrl+K) global search modal — now with typeahead.
 *
 *   - Empty input → shows recent queries (localStorage) + 8 popular tags.
 *   - >=2 chars   → debounced /api/search hits with question + user buckets.
 *   - Enter or "see all" → navigates to /search?q=<q> and stores the query.
 *   - Tag click   → navigates to /t/<tag>.
 */
export default function SearchModal({ open, onClose }) {
    const [q, setQ] = useState("");
    const [recent, setRecent] = useState([]);
    const [tags, setTags] = useState([]);
    const [results, setResults] = useState(null);
    const inputRef = useRef(null);
    const debounceRef = useRef(null);
    const nav = useNavigate();

    // Hydrate recent + popular tags lazily on first open.
    useEffect(() => {
        if (!open) return;
        setQ("");
        setResults(null);
        try {
            const stored = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
            if (Array.isArray(stored)) setRecent(stored.slice(0, MAX_RECENT));
        } catch { /* private mode */ }
        setTimeout(() => inputRef.current?.focus(), 30);
        // Popular tags — best-effort. Empty array on failure so the panel still shows recent.
        api.get("/tags/popular?limit=8").then((r) => setTags(r.data?.tags || [])).catch(() => setTags([]));
    }, [open]);

    // Debounced live search.
    useEffect(() => {
        if (!open) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const term = q.trim();
        if (term.length < 2) { setResults(null); return; }
        debounceRef.current = setTimeout(async () => {
            try {
                const r = await api.get(`/search?q=${encodeURIComponent(term)}&limit=5`);
                setResults(r.data || null);
            } catch { setResults(null); }
        }, 220);
        return () => clearTimeout(debounceRef.current);
    }, [q, open]);

    if (!open) return null;

    const persistRecent = (term) => {
        const next = [term, ...recent.filter((x) => x.toLowerCase() !== term.toLowerCase())].slice(0, MAX_RECENT);
        setRecent(next);
        try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* noop */ }
    };

    const submit = (term) => {
        const v = (term ?? q).trim();
        if (!v) return;
        persistRecent(v);
        nav(`/search?q=${encodeURIComponent(v)}`);
        onClose();
    };

    const removeRecent = (e, term) => {
        e.preventDefault(); e.stopPropagation();
        const next = recent.filter((x) => x !== term);
        setRecent(next);
        try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* noop */ }
    };

    const RowLink = ({ to, icon: Icon, tint, children, testid }) => (
        <Link
            key={to}
            to={to}
            onClick={onClose}
            data-testid={testid}
            className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 group transition-colors"
        >
            <span className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${tint}`}>
                <Icon size={14} weight="duotone" />
            </span>
            <span className="flex-1 text-sm text-slate-200 group-hover:text-white truncate">{children}</span>
            <ArrowRight size={13} weight="bold" className="text-slate-600 opacity-0 group-hover:opacity-100 group-hover:text-cyan-400 transition-opacity shrink-0" />
        </Link>
    );

    const SectionLabel = ({ icon: Icon, children }) => (
        <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold flex items-center gap-1.5">
            {Icon && <Icon size={11} weight="bold" />} {children}
        </div>
    );

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Site search"
            className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-start justify-center p-4 pt-[13vh]"
            onClick={onClose}
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
            data-testid="search-modal"
        >
            <form
                onSubmit={(e) => { e.preventDefault(); submit(); }}
                onClick={(e) => e.stopPropagation()}
                className="hc-cmdk-enter w-full max-w-2xl bg-[#0d1424] rounded-sm ring-1 ring-white/10 shadow-2xl shadow-black/60 max-h-[72vh] overflow-hidden flex flex-col"
            >
                <div className="flex items-center gap-3 px-4 py-3.5 border-b border-white/8">
                    <MagnifyingGlass size={19} className="text-cyan-400 shrink-0" />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="grep /community ..."
                        className="flex-1 bg-transparent outline-none text-slate-100 placeholder:text-slate-600 text-base sm:text-lg font-mono"
                        data-testid="search-modal-input"
                    />
                    <kbd className="hidden sm:inline-flex items-center text-[9px] uppercase tracking-widest text-slate-500 bg-white/5 ring-1 ring-white/10 rounded-md px-1.5 py-1">esc</kbd>
                    <button type="button" onClick={onClose} className="sm:hidden text-slate-400 hover:text-slate-200">
                        <X size={16} />
                    </button>
                </div>

                {/* Empty state — recent + popular tags */}
                {!q.trim() && (
                    <div className="overflow-y-auto pb-2">
                        {recent.length > 0 && (
                            <>
                                <SectionLabel icon={ClockCounterClockwise}>recent</SectionLabel>
                                <div className="flex flex-wrap gap-1.5 px-4 py-1.5" data-testid="search-modal-recent">
                                    {recent.map((r) => (
                                        <button
                                            key={r}
                                            type="button"
                                            onClick={() => submit(r)}
                                            className="group inline-flex items-center gap-1.5 bg-white/5 ring-1 ring-white/10 hover:ring-cyan-500/40 hover:bg-white/[0.08] text-slate-300 text-xs font-mono px-2.5 py-1 rounded-full transition-all"
                                        >
                                            <span>{r}</span>
                                            <span onClick={(e) => removeRecent(e, r)} className="text-slate-600 group-hover:text-rose-400 transition-colors">
                                                <X size={10} weight="bold" />
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                        {tags.length > 0 && (
                            <>
                                <SectionLabel icon={Hash}>popular tags</SectionLabel>
                                <div className="flex flex-wrap gap-1.5 px-4 py-1.5" data-testid="search-modal-tags">
                                    {tags.map((t) => (
                                        <Link
                                            key={t.name}
                                            to={`/t/${encodeURIComponent(t.name)}`}
                                            onClick={onClose}
                                            className="inline-flex items-center gap-1.5 bg-cyan-500/10 ring-1 ring-cyan-500/20 hover:ring-cyan-500/50 hover:bg-cyan-500/15 text-cyan-300 text-xs font-mono px-2.5 py-1 rounded-full transition-all"
                                        >
                                            <span>#{t.name}</span>
                                            <span className="text-cyan-500/60">{t.count}</span>
                                        </Link>
                                    ))}
                                </div>
                            </>
                        )}
                        <div className="px-4 pt-3 pb-1.5 text-[11px] text-slate-600 flex items-center gap-1.5">
                            <kbd className="inline-flex items-center text-[9px] text-slate-500 bg-white/5 ring-1 ring-white/10 rounded px-1.5 py-0.5">↵</kbd>
                            searches questions, news, bounties, tracks &amp; people
                        </div>
                    </div>
                )}

                {/* Live results */}
                {q.trim() && (
                    <div className="overflow-y-auto pb-1" data-testid="search-modal-results">
                        {!results && (
                            <div className="px-4 py-4 text-xs font-mono text-slate-500 flex items-center gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" /> searching…
                            </div>
                        )}
                        {results && (results.questions || []).length > 0 && (
                            <>
                                <SectionLabel icon={ChatCircle}>questions</SectionLabel>
                                {results.questions.slice(0, 5).map((r) => (
                                    <RowLink key={r.question_id} to={`/q/${r.question_id}`} icon={ChatCircle} tint="bg-cyan-500/10 text-cyan-400">
                                        {r.title}
                                    </RowLink>
                                ))}
                            </>
                        )}
                        {results && (results.users || []).length > 0 && (
                            <>
                                <SectionLabel icon={User}>people</SectionLabel>
                                {results.users.slice(0, 3).map((u) => (
                                    <RowLink key={u.user_id || u.username} to={`/u/${u.username}`} icon={User} tint="bg-violet-500/10 text-violet-400">
                                        @{u.username}
                                    </RowLink>
                                ))}
                            </>
                        )}
                        {results && (results.tracks || []).length > 0 && (
                            <div data-testid="search-modal-tracks">
                                <SectionLabel icon={Target}>career tracks</SectionLabel>
                                {results.tracks.slice(0, 4).map((t) => (
                                    <RowLink key={t.slug} to={`/tracks/${t.slug}`} icon={Target} tint="bg-emerald-500/10 text-emerald-400">
                                        {t.title}
                                    </RowLink>
                                ))}
                            </div>
                        )}
                        {results && (results.domains || []).length > 0 && (
                            <div data-testid="search-modal-domains">
                                <SectionLabel icon={GraduationCap}>academy domains</SectionLabel>
                                {results.domains.slice(0, 4).map((d) => (
                                    <RowLink key={d.slug} to={`/learn/${d.slug}`} icon={GraduationCap} tint="bg-amber-500/10 text-amber-400">
                                        {d.title}
                                    </RowLink>
                                ))}
                            </div>
                        )}
                        <button
                            type="button"
                            onClick={() => submit()}
                            className="w-full flex items-center gap-2 px-4 py-3 mt-1 border-t border-white/8 text-[11px] uppercase tracking-widest font-mono text-cyan-300 hover:bg-cyan-500/5 transition-colors"
                            data-testid="search-modal-seeall"
                        >
                            <MagnifyingGlass size={12} weight="bold" />
                            see all results for &quot;{q}&quot;
                            <ArrowRight size={12} weight="bold" className="ml-auto" />
                        </button>
                    </div>
                )}
            </form>
        </div>
    );
}
